import { ClientActionService } from "@cervval.js/client-action/src/services/client.action.service";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { CustomElement } from "@cervval.js/client-html5-spa/navigation";
import { CjsButton } from "@cervval.js/client-html5-ui/src/components/button/button";
import { CjsDropdownButton } from "@cervval.js/client-html5-ui/src/components/dropdownButton/dropdownButton";
import { CjsIconFa } from "@cervval.js/client-html5-ui/src/components/icon/iconFa";
import { ACTION_SERVICE } from "@cervval.js/common-action/src/services/action.service";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { SECONDS_IN_ONE_MINUTE } from "@cervval.js/common/src/tools/time.tools";
import { add, beginOf, format, subtract, TODAY, UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools";
import { BookingProfessional } from "@ehealth_tools/common-booking/src/data/booking.dto";
import { BOOKING_SERVICE } from "@ehealth_tools/common-booking/src/services/abstractCommonBooking.service";
import { AbstractClientBookingService } from "../../services/abstractClientBooking.service";
import { _I18nBookingSlider } from "./bookingSlider.i18n";
import { BookingSliderElement } from "./bookingSliderElement/bookingSliderElement";
export interface BookingSliderOptions {
    /** Origin date of the bookingSlider */
    from?: UnixTimestamp;
    /** Selected date */
    selected?: UnixTimestamp;
    /** Duration of the slot in second */
    slotDuration: number;
    onSelect?: (ts: UnixTimestamp) => void;
    /** Profession concerned by the booking slider */
    professional?: BookingProfessional;
}

const defaultBookingSliderOptions: BookingSliderOptions = {
    slotDuration: 15 * SECONDS_IN_ONE_MINUTE
};


@CustomElement({
    name: "booking-slider",
    style: require("./bookingSlider.scss"),
    template: require("./bookingSlider.html"),
    i18n: require("./bookingSlider.i18n").I18nBookingSlider()
})
export class BookingSlider extends WebComponent<_I18nBookingSlider> {

    @Injected(BOOKING_SERVICE)
    private _bookingService!: AbstractClientBookingService;

    @Injected(ACTION_SERVICE)
    private _actionService!: ClientActionService<unknown, unknown>;

    @TypedWebComponentOptions(defaultBookingSliderOptions)
    public readonly options!: WebComponentOptions<BookingSliderOptions>;

    @TypedHtmlElements({ ensureLoadedClasses: [CjsButton, CjsDropdownButton] })
    public readonly components!: {
        bookingContainer: HTMLDivElement,
        previousWeek: CjsIconFa,
        nextWeek: CjsIconFa
    };

    public override init(): Promise<void> | void {

        this.components.previousWeek.options.setAndRefreshIfChanged({
            name: "fa-chevron-left"
        });

        this.components.nextWeek.options.setAndRefreshIfChanged({
            name: "fa-chevron-right"
        });

        this.components.previousWeek.clickEvent.on(() => {
            this.options.setAndRefreshIfChanged({
                from: subtract(this.options.from ?? TODAY(), { day: 7 }).uts
            });
        });

        this.components.nextWeek.clickEvent.on(() => {
            this.options.setAndRefreshIfChanged({
                from: add(this.options.from ?? TODAY(), { day: 7 }).uts
            });
        });
    }

    public override refresh(reason?: "init" | "pageShow" | "action" | string): void {
        this.components.bookingContainer.innerHTML = "";
        if (this.options.professional == null) { return; }
        const now = beginOf(this.options.from ?? TODAY(), "day").uts;
        const in7days = add(now, { day: 7 }).uts;
        let date = now;
        while (date < in7days) {
            this.components.bookingContainer.append(new BookingSliderElement({
                userId: this.options.professional.id,
                dayTime: date,
                label: format(date, "dd LLL"), onSelect: (ts) => {
                    this.options.setAndRefreshIfChanged({ selected: ts });
                    this.options.onSelect?.(ts);
                },
                slotDuration: this.options.slotDuration ?? defaultBookingSliderOptions.slotDuration,
                selected: this.options.selected && date === beginOf(this.options.selected, "day").uts ? this.options.selected : undefined // If the from is the current day, pass it to the slider
            }));
            date = add(date, { day: 1 }).uts;
        }
    }


}